import {
  earningsApi,
  payoutsApi,
  type EarningsEntry,
  type EarningsSummary,
  type MyPayoutStatement,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { CrewLayout } from '@/components/Layout';
import { Nav } from '@/components/Nav';
import { OfflineIndicator } from '@/offline';
import { crewNavItems } from '@/pages/navItems';
import { Button } from '@/components/Button';
import { CrewActionBar, CrewMessage } from '@/components/CrewUi';
import { formatDate, formatPeso, humanize } from '@/lib/format';
import { formatPayPeriodRange } from '@/lib/earningsPeriod';

const ENTRY_STATE_LABELS: Record<string, string> = {
  held: 'Waiting for signature',
  accrued: 'Waiting approval',
  approved: 'Approved',
  paid: 'Paid',
};

const RUN_STATUS_LABELS: Record<string, string> = {
  draft: 'Being prepared',
  finalized: 'Approved',
  paid: 'Paid',
};

function tripsLabel(n: number): string {
  return `${n} trip${n === 1 ? '' : 's'}`;
}

/** "This period · 16–31 Oct" for the current pay fortnight (Manila). */
function earningsPeriodTitle(summary: EarningsSummary | null | undefined): string {
  if (!summary?.periodStart || !summary.periodEnd) return 'This period';
  return `This period · ${formatPayPeriodRange(summary.periodStart, summary.periodEnd)}`;
}

function entryAmount(h: EarningsEntry): number {
  return Number(h.overrideAmount ?? h.baseAmount) + Number(h.extraStopAmount ?? 0);
}

export function CrewEarnings() {
  const { data: summary, loading, error, reload } = useAsync(() => earningsApi.summary(), []);
  const { data: history, error: historyError, reload: reloadHistory } = useAsync(() => earningsApi.history(), []);
  const { data: statements, reload: reloadStatements } = useAsync(() => payoutsApi.myStatements(), []);

  const retryAll = () => {
    void reload();
    void reloadHistory();
    void reloadStatements();
  };

  return (
    <CrewLayout>
      <OfflineIndicator />
      <div className="crew-content">
        <Nav items={crewNavItems} variant="crew" />

        <div className="earnings-page">
          <h2 className="crew-title">Earnings</h2>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : error ? (
            <div className="crew-empty-card">
              <p>Couldn&apos;t load your earnings. Check your signal and try again.</p>
              <CrewActionBar>
                <Button type="button" variant="crew" size="crew" fullWidth onClick={retryAll}>
                  Try again
                </Button>
              </CrewActionBar>
            </div>
          ) : (
            <>
              <div className="crew-card earnings-hero">
                <span className="crew-label">{earningsPeriodTitle(summary)}</span>
                <span className="earnings-amount crew-num">
                  {formatPeso(summary?.periodEarnings ?? summary?.totalEarnings ?? 0)}
                </span>
                <span className="earnings-trips">
                  {tripsLabel(summary?.periodTripsCompleted ?? summary?.tripsCompleted ?? 0)} this period
                </span>
                {Number(summary?.pendingPayout ?? 0) > 0 && (
                  <span className="earnings-pending crew-num">Waiting to be paid: {formatPeso(summary?.pendingPayout)}</span>
                )}
                {summary?.periodEarnings != null && (
                  <span className="earnings-alltime crew-num">
                    All time: {formatPeso(summary.totalEarnings)} · {tripsLabel(summary.tripsCompleted)}
                  </span>
                )}
              </div>

              {historyError ? (
                <CrewMessage kind="error">Couldn&apos;t load your earnings history. Tap Try again above or reopen this page.</CrewMessage>
              ) : history && history.length > 0 ? (
                <div className="earnings-history">
                  <span className="crew-section-label">History</span>
                  {history.map((h: EarningsEntry) => (
                    <div key={h.id} className="crew-card earnings-row">
                      <div className="earnings-row-main">
                        <span>
                          {formatDate(h.createdAt)} · {humanize(h.role)}
                          {h.entryKind === 'correction' && ' · Corrected amount'}
                          {h.entryKind === 'adjustment' && ' · Pay adjustment'}
                        </span>
                        <span className={`crew-chip ${h.state === 'approved' || h.state === 'paid' ? 'crew-chip-ok' : 'crew-chip-amber'}`}>
                          {ENTRY_STATE_LABELS[h.state] ?? humanize(h.state)}
                        </span>
                      </div>
                      <span className="earnings-row-amount crew-num">{formatPeso(entryAmount(h))}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="crew-muted">Complete trips to see your earnings here.</p>
              )}

              {statements && statements.length > 0 && (
                <div className="earnings-history">
                  <span className="crew-section-label">Payout statements</span>
                  {statements.map((s: MyPayoutStatement) => (
                    <div key={s.id} className="crew-card earnings-row">
                      <div className="earnings-row-main">
                        <span>{s.periodLabel ?? `${formatDate(s.periodStart)} – ${formatDate(s.periodEnd)}`}</span>
                        <span className={`crew-chip ${s.runStatus === 'draft' ? 'crew-chip-amber' : 'crew-chip-ok'}`}>
                          {RUN_STATUS_LABELS[s.runStatus] ?? humanize(s.runStatus)}
                        </span>
                      </div>
                      <span className="earnings-row-amount crew-num">{formatPeso(s.netTotal)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        .earnings-hero { display: flex; flex-direction: column; gap: var(--cs-1); }
        .earnings-hero .crew-label { margin: 0; }
        .earnings-amount {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1.15;
          color: var(--crew-signal);
          overflow-wrap: anywhere;
        }
        .earnings-trips { font-size: var(--crew-fs-body); }
        .earnings-pending { color: var(--crew-amber); font-weight: 700; font-size: var(--crew-fs-body); }
        .earnings-alltime { color: var(--crew-muted); font-size: var(--crew-fs-support); }
        .earnings-row { display: flex; align-items: center; justify-content: space-between; gap: var(--cs-3); }
        .earnings-row-main { display: flex; flex-direction: column; align-items: flex-start; gap: var(--cs-2); min-width: 0; }
        .earnings-row-amount { font-weight: 700; font-size: var(--crew-fs-card); white-space: nowrap; }
      `}</style>
    </CrewLayout>
  );
}
